import { ChangeDetectionStrategy, Component } from '@angular/core';
import { UserInfoFacade } from '@features/user-info/facades/user-info/user-info.facade';
import { TranslateService } from '@ngx-translate/core';
import { AuthFacade } from '@rd/core/auth/store/facades/auth.facade';
import { DateService } from '@rd/core/services/date/date.service';
import { UrlUtilsService } from '@rd/core/services/url-utils/url-utils.service';
import { BannerImageConfig } from '@rd/shared-legacy/widgets/banner-image/banner-image.component';
import { UserInfo } from '@rd/types';
import { isNotNull } from '@rd/utils/isNotNull';
import { ANZFeatureFlagService } from '@rd/wl/anz/services/anz-feature-flag.service';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

@Component({
  selector: 'rd-anz-banner',
  templateUrl: './banner.component.html',
  styleUrls: ['./banner.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AnzBannerComponent {
  bannerImageConfig: BannerImageConfig = {
    jpg: {
      desktop: this.urlUtilsService.imageUrl('/images/homepage-desktop.jpg'),
      mobile: this.urlUtilsService.imageUrl('/images/homepage-mobile.jpg'),
    },
    avif: {
      desktop: this.urlUtilsService.imageUrl('/images/homepage-desktop.avif'),
      mobile: this.urlUtilsService.imageUrl('/images/homepage-mobile.avif'),
    },
    webp: {
      desktop: this.urlUtilsService.imageUrl('/images/homepage-desktop.webp'),
      mobile: this.urlUtilsService.imageUrl('/images/homepage-mobile.webp'),
    },
  };

  constructor(
    protected urlUtilsService: UrlUtilsService,
    protected translateService: TranslateService,
    protected dateService: DateService,
    protected userInfoFacade: UserInfoFacade,
    protected authFacade: AuthFacade,
    public featureFlagService: ANZFeatureFlagService,
  ) {}

  isLoggedIn$: Observable<boolean> = this.authFacade.isLoggedIn$;
  userInfo$: Observable<UserInfo | null> = this.userInfoFacade.userInfo$;
  personalizationMessage$: Observable<string> = this.userInfo$.pipe(
    filter(isNotNull),
    map((userInfo: UserInfo) => this.getPersonalizationMessage(userInfo)),
  );
  welcomeMessage$: Observable<string> = this.userInfo$.pipe(
    filter(isNotNull),
    map((userInfo: UserInfo) => this.getWelcomeMessage(userInfo)),
  );

  getPersonalizationMessage(userInfo: UserInfo): string {
    const { first_name, last_name } = userInfo;
    const userName = first_name || last_name;

    return this.translateService.instant(
      'homepage.banner.personalization_title',
      {
        userName,
      },
    );
  }

  getWelcomeMessage(userInfo: UserInfo): string {
    const { birthdate, first_name, last_name } = userInfo;
    const userName = first_name || last_name;

    return this.dateService.isDateTodayInUserTimezone(birthdate)
      ? this.translateService.instant('homepage.banner.birthday_title', {
          userName,
        })
      : this.translateService.instant('homepage.banner_title');
  }
}
